// [MCV-1872] Team, Provider Associations > Invitations and Peer References share
// one status pill and one Resend/Revoke treatment, so a status reads the same
// and the actions look the same wherever an invitation or request is listed.
import { Pill } from '~/components';
import type { PillTypes } from '~/components/Pill/Pill';

export const INVITATION_STATUS_PILL_TYPE: Record<string, PillTypes> = {
  PENDING: 'warning',
  ACCEPTED: 'success',
  RECEIVED: 'success',
  DECLINED: 'danger',
  REVOKED: 'danger',
  EXPIRED: 'danger',
  EMAIL_FAILED: 'danger',
};

/** 'EMAIL_FAILED' -> 'Email Failed', 'PENDING' -> 'Pending'. */
export const formatInvitationStatus = (status: string | null | undefined): string => {
  if (!status) return '';
  return status
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const InvitationStatusPill = ({ status, cyTag }: { status: string; cyTag?: string }) => (
  <Pill
    label={formatInvitationStatus(status)}
    type={INVITATION_STATUS_PILL_TYPE[status] ?? 'neutral'}
    cyTag={cyTag}
  />
);

const RESEND_CLASS =
  'text-sm font-medium text-primary-50 hover:text-primary-50/80 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50';
const REVOKE_CLASS =
  'text-sm font-medium text-error hover:text-error/80 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50';

type InvitationActionLinksProps = {
  /** Omit to hide Resend. */
  onResend?: () => void;
  /** Omit to hide Revoke. */
  onRevoke?: () => void;
  /** Disables both buttons. */
  disabled?: boolean;
  resendDisabled?: boolean;
  revokeDisabled?: boolean;
  resendCyTag?: string;
  revokeCyTag?: string;
};

export const InvitationActionLinks = ({
  onResend,
  onRevoke,
  disabled = false,
  resendDisabled = false,
  revokeDisabled = false,
  resendCyTag,
  revokeCyTag,
}: InvitationActionLinksProps) => {
  if (!onResend && !onRevoke) return null;

  return (
    <div className="flex items-center justify-end gap-3">
      {onResend && (
        <button
          type="button"
          data-cy={resendCyTag}
          disabled={disabled || resendDisabled}
          onClick={onResend}
          className={RESEND_CLASS}
        >
          Resend
        </button>
      )}
      {onRevoke && (
        <button
          type="button"
          data-cy={revokeCyTag}
          disabled={disabled || revokeDisabled}
          onClick={onRevoke}
          className={REVOKE_CLASS}
        >
          Revoke
        </button>
      )}
    </div>
  );
};
